import { ClipboardDocumentIcon, DocumentTextIcon } from '@heroicons/react/20/solid'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AddToCalendarButton from '@/pages/centrumSzkolen/common/AddToCalendarButton'
import { buildGoogleCalendarEventDescription } from '@/pages/centrumSzkolen/common/buildTrainingIcs'
import { buildMyProjectCalendarPayload } from './buildMyProjectCalendarPayload'

const secondaryButtonClassName = 'inline-flex items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50'

const copyText = async text => {
	try {
		if (navigator.clipboard?.writeText) {
			await navigator.clipboard.writeText(text)
			return true
		}
	} catch {
		// Fall back when the clipboard API is unavailable.
	}

	try {
		const area = document.createElement('textarea')
		area.value = text
		area.setAttribute('readonly', '')
		area.style.position = 'fixed'
		area.style.left = '-9999px'
		document.body.appendChild(area)
		area.select()
		const copied = document.execCommand('copy')
		document.body.removeChild(area)
		return copied
	} catch {
		return false
	}
}

function EventTextDialog({ text, onClose }) {
	const { t } = useTranslation()
	const [draft, setDraft] = useState(text)
	const [copied, setCopied] = useState(false)
	const canCopy = Boolean(draft.trim())

	useEffect(() => {
		const onKeyDown = event => {
			if (event.key === 'Escape') onClose()
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [onClose])

	const handleCopy = async () => {
		if (!canCopy) return
		const didCopy = await copyText(draft)
		if (didCopy) setCopied(true)
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
			<div className="absolute inset-0 bg-black/40" onClick={onClose} />
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="event-text-title"
				className="relative flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl"
			>
				<div className="border-b border-gray-200 px-4 py-3">
					<h3 id="event-text-title" className="text-base font-semibold text-gray-900">
						{t('myProjects.details.eventText.title', { defaultValue: 'Tekst wydarzenia' })}
					</h3>
				</div>
				<div className="min-h-0 flex-1 overflow-auto px-4 py-3">
					<textarea
						value={draft}
						onChange={event => {
							setDraft(event.target.value)
							setCopied(false)
						}}
						rows={16}
						className="block w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
					/>
				</div>
				<div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
					<button
						type="button"
						onClick={handleCopy}
						disabled={!canCopy}
						className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{copied
							? t('myProjects.details.eventText.copied', { defaultValue: 'Skopiowano' })
							: t('myProjects.details.eventText.copy', { defaultValue: 'Skopiuj do schowka' })}
					</button>
					<button
						type="button"
						onClick={onClose}
						className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
					>
						{t('myProjects.details.eventText.close', { defaultValue: 'Zamknij' })}
					</button>
				</div>
			</div>
		</div>
	)
}

export default function MyProjectDetailsCard({ project, statusLabel, createdDate }) {
	const { t } = useTranslation()
	const [eventTextOpen, setEventTextOpen] = useState(false)
	const [eventCopied, setEventCopied] = useState(false)
	const stepsCount = Array.isArray(project?.steps) ? project.steps.length : 0
	const calendarPayload = useMemo(() => buildMyProjectCalendarPayload(project), [project])
	const eventDescription = useMemo(
		() => buildGoogleCalendarEventDescription(calendarPayload),
		[calendarPayload],
	)

	useEffect(() => {
		if (!eventCopied) return undefined
		const timer = window.setTimeout(() => setEventCopied(false), 2000)
		return () => window.clearTimeout(timer)
	}, [eventCopied])

	const handleCopyEvent = async () => {
		if (!eventDescription.trim()) return
		const didCopy = await copyText(eventDescription)
		if (didCopy) setEventCopied(true)
	}

	return (
		<div className="mb-5 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<h1 className="text-lg font-semibold text-gray-900">{project?.nazwa || '—'}</h1>
				<div className="flex flex-wrap items-center gap-2">
					<AddToCalendarButton training={calendarPayload} />
					<button
						type="button"
						onClick={() => setEventTextOpen(true)}
						className={secondaryButtonClassName}
					>
						<DocumentTextIcon className="size-4" aria-hidden="true" />
						{t('myProjects.details.eventText.open', { defaultValue: 'Tekst wydarzenia' })}
					</button>
					<button
						type="button"
						onClick={handleCopyEvent}
						disabled={!eventDescription.trim()}
						className={`${secondaryButtonClassName} disabled:cursor-not-allowed disabled:opacity-60`}
					>
						<ClipboardDocumentIcon className="size-4" aria-hidden="true" />
						{eventCopied
							? t('myProjects.details.eventText.copied', { defaultValue: 'Skopiowano' })
							: t('myProjects.details.eventText.copyEvent', { defaultValue: 'Opis do schowka' })}
					</button>
				</div>
			</div>
			{eventTextOpen ? (
				<EventTextDialog
					text={eventDescription}
					onClose={() => setEventTextOpen(false)}
				/>
			) : null}

			<dl className="mt-4 grid grid-cols-1 gap-3 border-t border-gray-100 pt-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
						{t('projects.form.fields.status')}
					</dt>
					<dd className="mt-0.5 text-gray-900">{statusLabel || '—'}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
						{t('projects.list.table.stepsCount', { defaultValue: 'Etapy' })}
					</dt>
					<dd className="mt-0.5 text-gray-900">{stepsCount}</dd>
				</div>
				<div>
					<dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
						{t('projects.details.fields.created')}
					</dt>
					<dd className="mt-0.5 text-gray-900">{createdDate || '—'}</dd>
				</div>
			</dl>
		</div>
	)
}
